package com.rosario.juridico.ui.screens

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.gestures.detectHorizontalDragGestures
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.zIndex
import androidx.compose.ui.window.Dialog
import com.rosario.juridico.getPlatform
import com.rosario.juridico.openUrl
import com.rosario.juridico.platform.agregarEventoCalendario
import com.rosario.juridico.platform.localDateToMillis
import juridico5.composeapp.generated.resources.Res
import kotlinx.datetime.*
import org.jetbrains.compose.resources.ExperimentalResourceApi

// ─────────────────────────────────────────────────────────────────────────────
// ZONA HORARIA
// ─────────────────────────────────────────────────────────────────────────────

private val ARGENTINA_TZ: TimeZone by lazy {
    try {
        TimeZone.of("America/Argentina/Buenos_Aires")
    } catch (e: Exception) {
        TimeZone.of("UTC-3")
    }
}

fun hoyArgentina(): LocalDate = try {
    Clock.System.todayIn(ARGENTINA_TZ)
} catch (e: Exception) {
    Clock.System.todayIn(TimeZone.UTC)
}

// ─────────────────────────────────────────────────────────────────────────────
// MODELO AÑO-MES (reemplaza java.time.YearMonth)
// ─────────────────────────────────────────────────────────────────────────────

data class YearMonthKMP(val year: Int, val month: Month) {

    fun atDay(day: Int): LocalDate = LocalDate(year, month, day)

    fun lengthOfMonth(): Int {
        val nextMonth = if (month == Month.DECEMBER) LocalDate(year + 1, 1, 1)
        else LocalDate(year, month.number + 1, 1)
        val firstDay = LocalDate(year, month, 1)
        return (nextMonth.toEpochDays() - firstDay.toEpochDays())
    }

    fun plusMonths(n: Int): YearMonthKMP {
        var m = month.number - 1 + n
        var y = year + m / 12
        m = ((m % 12) + 12) % 12
        return YearMonthKMP(y, Month(m + 1))
    }

    fun minusMonths(n: Int): YearMonthKMP = plusMonths(-n)

    fun displayName(): String = "${nombreMes(month)} $year"

    companion object {
        fun now(): YearMonthKMP {
            val hoy = hoyArgentina()
            return YearMonthKMP(hoy.year, hoy.month)
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// MODELOS
// ─────────────────────────────────────────────────────────────────────────────

enum class TipoDia(val color: Color, val efecto: String, val label: String) {
    FERIADO(   Color(0xFFEF9A9A), "No corren plazos judiciales (Feriado)",             "Feriado"),
    INHABILE(  Color(0xFFFFF59D), "Día inhábil judicial (Sin actividad en tribunales)", "Inhábil Judicial"),
    FERIA(     Color(0xFF90CAF9), "Feria Judicial: Plazos suspendidos",                "Feria Judicial"),
    SUSPENSION(Color(0xFFFFCC80), "Suspensión excepcional de términos judiciales",     "Suspensión de Plazos"),
    HABIL(     Color.Transparent, "Día hábil judicial",                                "Día Hábil");

    companion object {
        fun fromString(tipo: String): TipoDia = when (tipo.lowercase().trim()) {
            "feriado"                                  -> FERIADO
            "inhabile", "inhabile judicial", "inhábil" -> INHABILE
            "feria", "feria judicial"                  -> FERIA
            "suspension", "suspensión"                 -> SUSPENSION
            else                                       -> HABIL
        }
    }
}

data class DiaEspecial(val fecha: LocalDate, val tipo: TipoDia, val descripcion: String)

// ─────────────────────────────────────────────────────────────────────────────
// PARSER
// ─────────────────────────────────────────────────────────────────────────────

object CalendarioParser {
    fun getHoy(): LocalDate = hoyArgentina()

    private var _cache: List<DiaEspecial>? = null

    @OptIn(ExperimentalResourceApi::class)
    suspend fun cargarTodo(): List<DiaEspecial> = kotlinx.coroutines.withContext(kotlinx.coroutines.Dispatchers.Default) {
        _cache?.let { return@withContext it }
        val dias = mutableMapOf<LocalDate, DiaEspecial>()
        try {
            val bytes = Res.readBytes("files/feriados_argentina.txt")
            val texto = bytes.decodeToString()
            texto.lines().forEach { line ->
                val cleanLine = line.trim()
                if (cleanLine.isNotEmpty() && !cleanLine.startsWith("#")) {
                    val partes = cleanLine.split("#")
                    try {
                        val fechaStr = partes[0].trim()
                        if (fechaStr.length >= 10) {
                            val fecha = LocalDate.parse(fechaStr.substring(0, 10))
                            val desc  = if (partes.size > 1) partes[1].trim() else "Feriado"
                            dias[fecha] = DiaEspecial(fecha, TipoDia.FERIADO, desc)
                        }
                    } catch (e: Exception) { }
                }
            }
        } catch (e: Exception) { }

        try {
            val bytes = Res.readBytes("files/calendario_judicial.txt")
            val texto = bytes.decodeToString()
            val regex = Regex("\\[(\\d{4}-\\d{2}-\\d{2})\\][\\s\\n]*tipo=([^\\n]*)[\\s\\n]*descripcion=([^\\n]*)")
            regex.findAll(texto).forEach { match ->
                try {
                    val fecha = LocalDate.parse(match.groupValues[1].trim())
                    val tipo  = TipoDia.fromString(match.groupValues[2].trim())
                    val desc  = match.groupValues[3].trim()
                    dias[fecha] = DiaEspecial(fecha, tipo, desc)
                } catch (e: Exception) { }
            }
        } catch (e: Exception) { }

        val resultado = dias.values.toList().sortedBy { it.fecha }
        _cache = resultado
        resultado
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE VISUAL DEL CALENDARIO
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun CalendarioJudicialComponent(
    diasEspeciales: List<DiaEspecial>,
    onActionRequest: ((LocalDate) -> Unit)? = null
) {
    val hoy = remember { hoyArgentina() }
    val isWeb = remember { getPlatform().name.contains("Web") }
    var mesActual by remember { mutableStateOf(YearMonthKMP.now()) }
    var diaSeleccionado by remember(diasEspeciales) { 
        mutableStateOf(
            diasEspeciales.find { it.fecha == hoy } ?: DiaEspecial(hoy, TipoDia.HABIL, "Día hábil judicial")
        ) 
    }
    var mostrarDialogo by remember { mutableStateOf(false) }
    var mostrarGoogle by remember { mutableStateOf(false) }
    val celdas = remember(mesActual) { buildCeldas(mesActual) }

    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
        Card(
            modifier = Modifier
                .widthIn(max = if (isWeb) 450.dp else 900.dp)
                .fillMaxWidth(if (isWeb) 0.95f else 1f)
                .padding(vertical = 8.dp)
                .animateContentSize(),
            shape    = RoundedCornerShape(16.dp),
            colors   = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
            elevation = CardDefaults.cardElevation(2.dp)
        ) {
            Column(modifier = Modifier.padding(if (isWeb) 12.dp else 16.dp)) {
                // Barra de Resumen Superior
                Surface(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 16.dp),
                    color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f),
                    shape = RoundedCornerShape(12.dp),
                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.primary.copy(alpha = 0.1f))
                ) {
                    AnimatedContent(
                        targetState = diaSeleccionado,
                        transitionSpec = {
                            (fadeIn(animationSpec = tween(220, delayMillis = 90)) + 
                             slideInHorizontally(initialOffsetX = { it / 4 }))
                            .togetherWith(fadeOut(animationSpec = tween(90)))
                        },
                        label = "ResumenFecha"
                    ) { targetDia ->
                        Row(
                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(36.dp)
                                    .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.1f), CircleShape),
                                contentAlignment = Alignment.Center
                            ) {
                                Crossfade(targetState = targetDia.tipo, label = "IconoTipo") { tipo ->
                                    Icon(
                                        imageVector = if (tipo == TipoDia.HABIL) Icons.Default.Balance else Icons.Default.EventBusy,
                                        contentDescription = null,
                                        tint = MaterialTheme.colorScheme.primary,
                                        modifier = Modifier.size(18.dp)
                                    )
                                }
                            }
                            
                            Spacer(Modifier.width(12.dp))
                            
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    text = "${targetDia.fecha.dayOfMonth} de ${nombreMes(targetDia.fecha.month)} ${targetDia.fecha.year}",
                                    style = MaterialTheme.typography.labelMedium,
                                    fontWeight = FontWeight.ExtraBold,
                                    color = MaterialTheme.colorScheme.primary,
                                    fontSize = if (isWeb) 11.sp else 12.sp
                                )
                                Text(
                                    text = targetDia.descripcion,
                                    style = MaterialTheme.typography.bodySmall,
                                    fontWeight = FontWeight.Medium,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    maxLines = 1,
                                    fontSize = if (isWeb) 10.sp else 11.sp
                                )
                            }

                            if (targetDia.tipo != TipoDia.HABIL) {
                                Surface(
                                    color = targetDia.tipo.color,
                                    shape = RoundedCornerShape(6.dp)
                                ) {
                                    Text(
                                        text = targetDia.tipo.label,
                                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 3.dp),
                                        style = MaterialTheme.typography.labelSmall,
                                        fontWeight = FontWeight.Bold,
                                        color = Color.Black,
                                        fontSize = 9.sp
                                    )
                                }
                            }
                        }
                    }
                }

                // Cabecera: Mes y Año (Estilo Google Calendar)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    OutlinedButton(
                        onClick = {
                            mesActual = YearMonthKMP.now()
                            diaSeleccionado = diasEspeciales.find { it.fecha == hoy } ?: DiaEspecial(hoy, TipoDia.HABIL, "Día hábil judicial")
                        },
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 14.dp, vertical = 0.dp),
                        modifier = Modifier.height(32.dp),
                        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                    ) {
                        Text(
                            "Hoy", 
                            style = MaterialTheme.typography.labelLarge, 
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }

                    Spacer(Modifier.width(8.dp))

                    IconButton(onClick = { mesActual = mesActual.minusMonths(1) }, modifier = Modifier.size(32.dp)) {
                        Icon(Icons.Default.ChevronLeft, "Anterior", tint = MaterialTheme.colorScheme.onSurfaceVariant)
                    }

                    IconButton(onClick = { mesActual = mesActual.plusMonths(1) }, modifier = Modifier.size(32.dp)) {
                        Icon(Icons.Default.ChevronRight, "Siguiente", tint = MaterialTheme.colorScheme.onSurfaceVariant)
                    }

                    Spacer(Modifier.width(16.dp))

                    Text(
                        text = mesActual.displayName(),
                        style = if (isWeb) MaterialTheme.typography.titleMedium else MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.ExtraBold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }

                Spacer(Modifier.height(12.dp))

                // Grilla de días (7 columnas)
                val rowsCount = (celdas.size / 7) + 1 
                val cellHeightValue = if (isWeb) 44 else 54
                val spacingValue = 4
                val gridHeight = (rowsCount * cellHeightValue + (rowsCount - 1) * spacingValue + 12).dp 

                LazyVerticalGrid(
                    columns = GridCells.Fixed(7),
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(gridHeight)
                        .pointerInput(Unit) {
                            var dragAccumulator = 0f
                            val dragThreshold = 200f
                            detectHorizontalDragGestures(
                                onDragEnd = { dragAccumulator = 0f },
                                onDragCancel = { dragAccumulator = 0f }
                            ) { change, dragAmount ->
                                change.consume()
                                dragAccumulator += dragAmount
                                if (dragAccumulator < -dragThreshold) {
                                    mesActual = mesActual.plusMonths(1)
                                    dragAccumulator = 0f
                                } else if (dragAccumulator > dragThreshold) {
                                    mesActual = mesActual.minusMonths(1)
                                    dragAccumulator = 0f
                                }
                            }
                        },
                    userScrollEnabled = false,
                    horizontalArrangement = Arrangement.spacedBy(spacingValue.dp),
                    verticalArrangement = Arrangement.spacedBy(spacingValue.dp)
                ) {
                    // Días de la semana
                    val daysOfWeek = listOf("Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom")
                    items(daysOfWeek) { day ->
                        Text(
                            text = day,
                            textAlign = TextAlign.Center,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary.copy(alpha = 0.7f),
                            modifier = Modifier.padding(bottom = 6.dp)
                        )
                    }

                    // Celdas de fechas
                    items(celdas) { fecha ->
                        if (fecha != null) {
                            val esp = diasEspeciales.find { it.fecha == fecha }
                            val esHoy = fecha == hoy
                            val isSelected = diaSeleccionado.fecha == fecha

                            DayCell(
                                fecha = fecha,
                                esHoy = esHoy,
                                isSelected = isSelected,
                                especial = esp,
                                onClick = {
                                    diaSeleccionado = esp ?: DiaEspecial(fecha, TipoDia.HABIL, "Día hábil judicial")
                                    mostrarDialogo = true
                                }
                            )
                        } else {
                            Box(modifier = Modifier.aspectRatio(1f))
                        }
                    }
                }

                Spacer(Modifier.height(20.dp))
                
                // Leyendas de colores
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.CenterHorizontally),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    LeyendaColor(TipoDia.FERIADO.color,    "Feriado")
                    LeyendaColor(TipoDia.INHABILE.color,   "Inhábil")
                    LeyendaColor(TipoDia.FERIA.color,      "Feria")
                    LeyendaColor(TipoDia.SUSPENSION.color, "Susp. Plazos")
                }
            }
        }
    }

    if (mostrarDialogo) {
        AlertDialog(
            onDismissRequest = { mostrarDialogo = false },
            shape = RoundedCornerShape(20.dp),
            containerColor = MaterialTheme.colorScheme.surface,
            icon = {
                Icon(
                    if (diaSeleccionado.tipo == TipoDia.HABIL) Icons.Default.Info else Icons.Default.Warning,
                    contentDescription = null,
                    tint = if (diaSeleccionado.tipo == TipoDia.HABIL) MaterialTheme.colorScheme.primary else Color(0xFFEF5350),
                    modifier = Modifier.size(32.dp)
                )
            },
            title = {
                Text(
                    text = "${diaSeleccionado.fecha.dayOfMonth} de ${nombreMes(diaSeleccionado.fecha.month)}",
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.ExtraBold,
                    color = MaterialTheme.colorScheme.onSurface
                )
            },
            text = {
                Column(
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Surface(
                        color = diaSeleccionado.tipo.color.copy(alpha = 0.15f),
                        shape = RoundedCornerShape(8.dp),
                        border = BorderStroke(1.dp, diaSeleccionado.tipo.color.copy(alpha = 0.3f))
                    ) {
                        Text(
                            text = diaSeleccionado.tipo.label,
                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                            style = MaterialTheme.typography.labelSmall,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    
                    Text(
                        text = diaSeleccionado.descripcion,
                        style = MaterialTheme.typography.bodyLarge,
                        fontWeight = FontWeight.Medium,
                        textAlign = TextAlign.Center
                    )
                    
                    Text(
                        text = diaSeleccionado.tipo.efecto,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = TextAlign.Center
                    )
                }
            },
            confirmButton = { 
                Button(
                    onClick = { mostrarGoogle = true },
                    shape = RoundedCornerShape(12.dp),
                    contentPadding = PaddingValues(horizontal = 20.dp, vertical = 10.dp)
                ) {
                    Icon(Icons.Default.CalendarMonth, null, Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Agendar Evento")
                }
            },
            dismissButton = { 
                TextButton(onClick = { mostrarDialogo = false }) { 
                    Text("Cerrar", fontWeight = FontWeight.Bold) 
                }
            }
        )
    }

    if (mostrarGoogle) {
        DialogGoogleCalendar(
            fecha = diaSeleccionado.fecha,
            onDismiss = { mostrarGoogle = false; mostrarDialogo = false }
        )
    }
}

@Composable
fun DialogGoogleCalendar(fecha: LocalDate, onDismiss: () -> Unit) {
    val opciones = listOf(
        OpcionAgenda("Audiencia", Icons.Default.Balance),
        OpcionAgenda("Mediación", Icons.Default.Handshake),
        OpcionAgenda("Vencimiento", Icons.Default.Alarm),
        OpcionAgenda("Entrevista", Icons.Default.Groups),
        OpcionAgenda("Feria", Icons.Default.Event),
        OpcionAgenda("Otro", Icons.Default.MoreHoriz)
    )
    
    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(24.dp),
        title = { 
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.CalendarMonth, null, tint = MaterialTheme.colorScheme.primary)
                Spacer(Modifier.width(12.dp))
                Text("Agendar Evento", style = MaterialTheme.typography.titleLarge)
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Text(
                    "Selecciona el tipo de evento para el ${fecha.dayOfMonth}/${fecha.monthNumber}:", 
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    opciones.chunked(2).forEach { fila ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            fila.forEach { opcion ->
                                FilledTonalButton(
                                    onClick = {
                                        agendarEnGoogle(fecha, opcion.titulo)
                                        onDismiss()
                                    },
                                    modifier = Modifier.weight(1f).height(48.dp),
                                    shape = RoundedCornerShape(12.dp),
                                    contentPadding = PaddingValues(horizontal = 8.dp)
                                ) {
                                    Icon(opcion.icono, null, modifier = Modifier.size(18.dp))
                                    Spacer(Modifier.width(8.dp))
                                    Text(
                                        opcion.titulo, 
                                        fontSize = 11.sp, 
                                        fontWeight = FontWeight.Bold,
                                        maxLines = 1
                                    )
                                }
                            }
                        }
                    }
                }
            }
        },
        confirmButton = { 
            TextButton(onClick = onDismiss) { 
                Text("Cancelar", fontWeight = FontWeight.Bold) 
            } 
        }
    )
}

private data class OpcionAgenda(val titulo: String, val icono: androidx.compose.ui.graphics.vector.ImageVector)

private fun agendarEnGoogle(fecha: LocalDate, titulo: String) {
    val millis = localDateToMillis(fecha)
    agregarEventoCalendario(
        titulo = titulo,
        fechaMillis = millis,
        descripcion = "Agendado desde Jurídico Rosario"
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

fun buildCeldas(mes: YearMonthKMP): List<LocalDate?> {
    val primerDia = mes.atDay(1).dayOfWeek
    val offset = when (primerDia) {
        DayOfWeek.MONDAY    -> 0; DayOfWeek.TUESDAY   -> 1; DayOfWeek.WEDNESDAY -> 2
        DayOfWeek.THURSDAY  -> 3; DayOfWeek.FRIDAY    -> 4; DayOfWeek.SATURDAY  -> 5
        DayOfWeek.SUNDAY    -> 6; else -> 0
    }
    val celdas = mutableListOf<LocalDate?>()
    repeat(offset) { celdas.add(null) }
    for (i in 1..mes.lengthOfMonth()) {
        celdas.add(mes.atDay(i))
    }
    while (celdas.size % 7 != 0) {
        celdas.add(null)
    }
    return celdas
}

fun nombreMes(m: Month): String = when(m) {
    Month.JANUARY -> "Enero"; Month.FEBRUARY -> "Febrero"; Month.MARCH -> "Marzo"
    Month.APRIL -> "Abril"; Month.MAY -> "Mayo"; Month.JUNE -> "Junio"
    Month.JULY -> "Julio"; Month.AUGUST -> "Agosto"; Month.SEPTEMBER -> "Septiembre"
    Month.OCTOBER -> "Octubre"; Month.NOVEMBER -> "Noviembre"; Month.DECEMBER -> "Diciembre"
    else -> ""
}

@Composable
fun DayCell(
    fecha: LocalDate,
    esHoy: Boolean,
    isSelected: Boolean,
    especial: DiaEspecial?,
    onClick: () -> Unit
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()
    val isWeb = remember { getPlatform().name.contains("Web") }
    
    val isEspecial = especial != null && especial.tipo != TipoDia.HABIL
    
    val scale by animateFloatAsState(
        targetValue = if (isSelected) 1.08f else 1f,
        animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow)
    )

    val backgroundColor by animateColorAsState(
        when {
            isSelected -> MaterialTheme.colorScheme.primary
            isEspecial -> especial!!.tipo.color.copy(alpha = 0.15f)
            isHovered -> MaterialTheme.colorScheme.primary.copy(alpha = 0.08f)
            else -> Color.Transparent
        }
    )

    val borderColor by animateColorAsState(
        when {
            isSelected -> Color.Transparent
            esHoy -> MaterialTheme.colorScheme.primary
            isEspecial -> especial!!.tipo.color.copy(alpha = 0.4f)
            else -> Color.Transparent
        }
    )

    val elevation = if (isSelected) 8.dp else 0.dp

    Surface(
        modifier = Modifier
            .then(
                if (isWeb) Modifier.height(44.dp) 
                else Modifier.aspectRatio(1f)
            )
            .graphicsLayer {
                scaleX = scale
                scaleY = scale
            }
            .zIndex(if (isSelected) 2f else if (isEspecial) 1f else 0f)
            .shadow(elevation, RoundedCornerShape(12.dp))
            .hoverable(interactionSource)
            .clickable(
                interactionSource = interactionSource,
                indication = LocalIndication.current,
                onClick = onClick
            ),
        shape = RoundedCornerShape(12.dp),
        color = backgroundColor,
        border = if ((esHoy || isEspecial) && !isSelected) BorderStroke(1.dp, borderColor) else null
    ) {
        Box(contentAlignment = Alignment.Center) {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Text(
                    text = fecha.dayOfMonth.toString(),
                    fontSize = if (isWeb) 14.sp else 15.sp,
                    fontWeight = if (esHoy || isEspecial || isSelected) FontWeight.ExtraBold else FontWeight.Medium,
                    color = when {
                        isSelected -> Color.White
                        esHoy -> MaterialTheme.colorScheme.primary
                        isEspecial -> MaterialTheme.colorScheme.onSurface
                        else -> MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                    }
                )
                
                // Indicador inferior (punto para hoy, barra para especial)
                if (!isSelected) {
                    when {
                        esHoy -> {
                            Box(
                                modifier = Modifier
                                    .padding(top = 2.dp)
                                    .size(4.dp)
                                    .clip(CircleShape)
                                    .background(MaterialTheme.colorScheme.primary)
                            )
                        }
                        isEspecial -> {
                            Box(
                                modifier = Modifier
                                    .padding(top = 2.dp)
                                    .width(12.dp)
                                    .height(3.dp)
                                    .clip(RoundedCornerShape(2.dp))
                                    .background(especial!!.tipo.color)
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun LeyendaColor(color: Color, label: String) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(
            modifier = Modifier
                .size(10.dp)
                .clip(RoundedCornerShape(3.dp))
                .background(color)
                .border(0.5.dp, color.copy(alpha = 0.8f), RoundedCornerShape(3.dp))
        )
        Spacer(Modifier.width(6.dp))
        Text(
            text = label, 
            fontSize = 11.sp, 
            fontWeight = FontWeight.Medium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}
